import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const particles = Array.from({ length: 12 }, (_, i) => ({
  id: i,
  tx: `${(Math.random() - 0.5) * 200}px`,
  ty: `${(Math.random() - 0.5) * 200}px`,
  delay: `${Math.random() * 3}s`,
  duration: `${2.5 + Math.random() * 2}s`,
  left: `${20 + Math.random() * 60}%`,
  top: `${20 + Math.random() * 60}%`,
  size: `${3 + Math.random() * 4}px`,
}));

const Hero = () => {
  const { t } = useLanguage();
  const [videoLoaded, setVideoLoaded] = useState(false);

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Loading Animation Overlay */}
      <div
        className="absolute inset-0 z-[1] flex items-center justify-center transition-opacity duration-[800ms] ease-out pointer-events-none"
        style={{ opacity: videoLoaded ? 0 : 1 }}
      >
        {/* Dark background with radial gold glow */}
        <div className="absolute inset-0 bg-background" />
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(circle at 50% 50%, hsl(38 85% 55% / 0.08) 0%, transparent 60%)',
            animation: 'hero-glow-pulse 3s ease-in-out infinite',
          }}
        />

        {/* Concentric pulsing rings */}
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="absolute rounded-full border-2"
            style={{
              width: '200px',
              height: '200px',
              borderColor: 'hsl(38 85% 55% / 0.4)',
              animation: `hero-ring-pulse ${3 + i * 0.4}s cubic-bezier(0.4, 0, 0.2, 1) infinite`,
              animationDelay: `${i * 0.8}s`,
            }}
          />
        ))}

        {/* Gold floating particles */}
        {particles.map((p) => (
          <div
            key={p.id}
            className="absolute rounded-full"
            style={{
              width: p.size,
              height: p.size,
              left: p.left,
              top: p.top,
              background: 'hsl(38 85% 55%)',
              boxShadow: '0 0 6px hsl(38 85% 55% / 0.6)',
              '--tx': p.tx,
              '--ty': p.ty,
              animation: `hero-particle-float ${p.duration} ease-in-out infinite`,
              animationDelay: p.delay,
            } as React.CSSProperties}
          />
        ))}

        {/* Shimmer text */}
        <div
          className="relative z-10 text-center"
          style={{ animation: 'hero-fade-up 1.2s ease-out forwards' }}
        >
          <p
            className="text-sm tracking-[0.3em] uppercase mb-4 font-medium"
            style={{
              color: 'hsl(38 85% 55% / 0.7)',
              animation: 'hero-fade-up 1s ease-out 0.3s both',
            }}
          >
            {t('hero.welcome')}
          </p>
          <h2
            className="font-serif text-4xl sm:text-5xl md:text-7xl font-bold"
            style={{
              background: 'linear-gradient(90deg, hsl(35 75% 45%), hsl(38 85% 55%), hsl(45 95% 70%), hsl(38 85% 55%), hsl(35 75% 45%))',
              backgroundSize: '200% auto',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              animation: 'hero-shimmer 3s linear infinite, hero-fade-up 1s ease-out 0.6s both',
            }}
          >
            Currylicious<br />Yum Yum
          </h2>
        </div>
      </div>

      {/* Background Video */}
      <div className="absolute inset-0 z-0" style={{ transform: 'translateZ(0)' }}>
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          onCanPlay={() => setVideoLoaded(true)}
          className="w-full h-[120%] object-cover transition-opacity duration-[800ms]"
          style={{
            opacity: videoLoaded ? 1 : 0,
            transform: 'translate3d(0, calc(var(--scroll-y, 0) * 0.5px), 0)',
            willChange: 'transform',
          }}
        >
          <source src="/videos/restaurant-1.mov" type="video/quicktime" />
          <source src="/videos/restaurant-1.mov" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/60 to-background"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 container-custom text-center pt-20 px-4">
        <div className="space-y-4 sm:space-y-6 max-w-4xl mx-auto">
          <p
            className="text-gold text-base sm:text-lg md:text-xl font-medium tracking-wider uppercase transition-all duration-700 ease-out"
            style={{
              opacity: videoLoaded ? 1 : 0,
              transform: videoLoaded ? 'translateY(0)' : 'translateY(20px)',
              transitionDelay: videoLoaded ? '600ms' : '0ms',
            }}
          >
            {t('hero.welcome')}
          </p>
          <h1
            className="heading-luxury text-4xl sm:text-5xl md:text-7xl lg:text-8xl text-foreground transition-all duration-700 ease-out"
            style={{
              textShadow: '0 0 40px hsl(var(--gold) / 0.3)',
              opacity: videoLoaded ? 1 : 0,
              transform: videoLoaded ? 'translateY(0)' : 'translateY(20px)',
              transitionDelay: videoLoaded ? '750ms' : '0ms',
            }}
          >
            Currylicious<br />Yum Yum
          </h1>
          <p
            className="text-xl sm:text-2xl md:text-3xl text-gold-light font-serif italic transition-all duration-700 ease-out"
            style={{
              opacity: videoLoaded ? 1 : 0,
              transform: videoLoaded ? 'translateY(0)' : 'translateY(20px)',
              transitionDelay: videoLoaded ? '900ms' : '0ms',
            }}
          >
            {t('hero.tagline')}
          </p>
          <p
            className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed px-4 transition-all duration-700 ease-out"
            style={{
              opacity: videoLoaded ? 1 : 0,
              transform: videoLoaded ? 'translateY(0)' : 'translateY(20px)',
              transitionDelay: videoLoaded ? '1050ms' : '0ms',
            }}
          >
            {t('hero.description')}
          </p>
          <div
            className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center pt-4 sm:pt-6 px-4 transition-all duration-700 ease-out"
            style={{
              opacity: videoLoaded ? 1 : 0,
              transform: videoLoaded ? 'translateY(0)' : 'translateY(20px)',
              transitionDelay: videoLoaded ? '1200ms' : '0ms',
            }}
          >
            <Button
              size="lg"
              className="group relative bg-gold hover:bg-gold-light text-background font-semibold text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 shadow-gold w-full sm:w-auto transform hover:scale-105 transition-all duration-300 overflow-hidden"
              style={{ boxShadow: 'var(--shadow-gold-glow)' }}
              onClick={() => document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' })}
            >
              <span className="relative z-10">{t('hero.bookNow')}</span>
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></span>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="group border-2 border-gold text-gold hover:bg-gold hover:text-background font-semibold text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 w-full sm:w-auto transform hover:scale-105 transition-all duration-300 relative overflow-hidden"
              onClick={() => document.getElementById('menu')?.scrollIntoView({ behavior: 'smooth' })}
            >
              <span className="relative z-10">{t('hero.seeMenus')}</span>
              <span className="absolute inset-0 bg-gold -translate-y-full group-hover:translate-y-0 transition-transform duration-300"></span>
            </Button>
          </div>

          {/* Scroll Indicator */}
          <div
            className="flex justify-center pt-1 transition-all duration-700 ease-out"
            style={{
              opacity: videoLoaded ? 1 : 0,
              transform: videoLoaded ? 'translateY(0)' : 'translateY(20px)',
              transitionDelay: videoLoaded ? '1350ms' : '0ms',
            }}
          >
            <a
              href="#about"
              aria-label="Scrolla ner till Om Oss"
              className="cursor-pointer animate-bounce"
              style={{
                filter: 'drop-shadow(0 0 10px #F5A524) drop-shadow(0 0 20px #F5A524) drop-shadow(0 0 30px #F5A524)',
                marginLeft: '28px',
              }}
            >
              <ChevronDown size={40} color="#F5A524" strokeWidth={3} className="mx-0 ml-[2px]" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
